<script setup>
// The "why" of one answer of the Max in the preview: the step the conversation is at, the attributes filled in so
// far and the tools called (with how each went). Folded by default, under the answer it explains.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { ChevronDown16 } from '@carbon/icons-vue';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'dashboard/components-next/polaris/ui/collapsible';

const props = defineProps({
  // { node, facts, tools: [{ name, status }], phase_changed }
  trace: { type: Object, required: true },
  // [{ node_key, title }] to say the step by its title
  steps: { type: Array, default: () => [] },
  // [{ key, label }] to say the attributes by their label
  factLabels: { type: Array, default: () => [] },
  // [{ id, label }]
  toolLabels: { type: Array, default: () => [] },
});
const { t } = useI18n();

const stepTitle = computed(
  () =>
    props.steps.find(step => step.node_key === props.trace.node)?.title ||
    props.trace.node ||
    ''
);
const facts = computed(() =>
  Object.entries(props.trace.facts || {})
    .filter(([, value]) => value !== null && value !== '')
    .map(([key, value]) => ({
      key,
      label: props.factLabels.find(fact => fact.key === key)?.label || key,
      value: typeof value === 'object' ? JSON.stringify(value) : String(value),
    }))
);
const tools = computed(() =>
  (props.trace.tools || []).map(tool => ({
    ...tool,
    label:
      props.toolLabels.find(item => item.id === tool.name)?.label || tool.name,
  }))
);
</script>

<template>
  <Collapsible class="grid gap-2" data-preview-why>
    <CollapsibleTrigger
      class="group flex w-fit items-center gap-1 rounded-polaris-md type-polaris-caption text-polaris-muted-foreground outline-none hover:text-polaris-foreground focus-visible:ring-[3px] focus-visible:ring-polaris-ring/50"
    >
      {{ t('POLARIS.PREVIEW.WHY.TRIGGER') }}
      <ChevronDown16
        class="size-3 transition-transform duration-150 group-data-[state=open]:rotate-180"
        aria-hidden="true"
      />
    </CollapsibleTrigger>
    <CollapsibleContent>
      <dl
        class="grid gap-2 rounded-polaris-md bg-polaris-muted p-3 type-polaris-caption"
      >
        <div class="grid gap-0.5">
          <dt class="text-polaris-muted-foreground">
            {{ t('POLARIS.PREVIEW.WHY.STEP') }}
          </dt>
          <dd data-why-step>
            {{ stepTitle || t('POLARIS.PREVIEW.WHY.NONE') }}
            <span
              v-if="trace.phase_changed"
              class="text-polaris-muted-foreground"
            >
              {{ t('POLARIS.PREVIEW.WHY.CHANGED') }}
            </span>
          </dd>
        </div>
        <div class="grid gap-0.5">
          <dt class="text-polaris-muted-foreground">
            {{ t('POLARIS.PREVIEW.WHY.FACTS') }}
          </dt>
          <dd v-if="!facts.length">{{ t('POLARIS.PREVIEW.WHY.NONE') }}</dd>
          <dd v-for="fact in facts" :key="fact.key" data-why-fact>
            {{ t('POLARIS.PREVIEW.WHY.FACT_LABEL', { label: fact.label }) }}
            <strong>{{ fact.value }}</strong>
          </dd>
        </div>
        <div class="grid gap-0.5">
          <dt class="text-polaris-muted-foreground">
            {{ t('POLARIS.PREVIEW.WHY.TOOLS') }}
          </dt>
          <dd v-if="!tools.length">{{ t('POLARIS.PREVIEW.WHY.NONE') }}</dd>
          <dd v-for="tool in tools" :key="tool.name" data-why-tool>
            {{ tool.label }}
            <span class="text-polaris-muted-foreground">
              {{
                t('POLARIS.PREVIEW.WHY.TOOL_STATUS', { status: tool.status })
              }}
            </span>
          </dd>
        </div>
      </dl>
    </CollapsibleContent>
  </Collapsible>
</template>
